import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { itIT } from '@mui/material/locale';
import CssBaseline from '@mui/material/CssBaseline';

// 1. Importiamo tutti i nostri mattoni fondamentali
import { AuthProvider } from './contexts/AuthContext';      // Fornisce lo stato di login a tutta l'app
import ProtectedRoute from './components/ProtectedRoute';  // La "guardia" per le pagine private
import Layout from './Layouts/Layout';                     // Il layout per le pagine private
import PublicLayout from './Layouts/PublicLayout';         // Il layout per le pagine pubbliche

// 2. Importiamo tutte le pagine dell'applicazione
import Login from './pages/Login';
import Verify2FAPage from './pages/Verify2FAPage';
import ResetPasswordPage from './pages/ResetPasswordPage';
import Home from './pages/Home';
import Users from './pages/Users';
import Profile from './pages/Profile';
import ChiamatePage from './pages/Calls';
import FileStoragePage from './pages/FileStoragePage'; 
import FeedbackForm from './pages/FeedbackForm';
import KPIMetricsPage from './pages/KPIMetricsPage';
import AnalyticsPage from './pages/AnalyticsPage';
import OrariUfficiPage from './pages/OrariUfficiPage';
import OrariTrasferimentoPage from './pages/OrariTrasferimentoPage';
import EventiPage from './pages/EventiPage';
import SettingsPage from './pages/SettingsPage';
import AnnouncementsPage from './pages/AnnouncementsPage';
import { useComuneConfig } from './contexts/ConfigContext';

// La configurazione del tema MUI è corretta
const theme = createTheme({
  palette: {
    mode: 'light',
    primary: {
      main: '#1976d2',
    },
    secondary: {
      main: '#dc004e',
    },
  },
}, itIT); // testi interni MUI (paginazione, chiudi, ...) in italiano

function App() {
  const comune = useComuneConfig();
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      {/* 3. AuthProvider avvolge tutto. Qualsiasi componente all'interno
          potrà usare l'hook `useAuth()` per sapere se l'utente è loggato. */}
      <AuthProvider>
        <Router>
          <Routes>
            {/* --- Sezione Rotte Pubbliche --- */}
            {/* Queste rotte sono accessibili da chiunque, anche senza login. */}
            <Route path="/login" element={<Login />} />
            <Route path="/reset-password/:token" element={<ResetPasswordPage />} />
            <Route path="/verify-2fa" element={<Verify2FAPage />} />

            {/* Questa è una rotta pubblica con un layout specifico. Corretto. */}
            <Route element={<PublicLayout />}>
              <Route path="/feedback/:callId" element={<FeedbackForm />} />
            </Route>

            {/* --- Sezione Rotte Protette --- */}
            {/* Questa è la rotta "genitore" per tutte le pagine private.
                Il componente `ProtectedRoute` agisce come un cancello: se l'utente
                non è loggato, lo rimanda a /login. Se è loggato, mostra il `Layout`. */}
            <Route
              path="/"
              element={
                <ProtectedRoute>
                  <Layout />
                </ProtectedRoute>
              }
            >
              {/* Queste rotte "figlie" vengono mostrate all'interno del `Layout`
                  solo se l'utente ha superato il controllo di `ProtectedRoute`. */}
              <Route index element={<Home />} />
              {/* /users is reachable by USER and ADMIN; the page and the
                  backend enforce the two-tier rule (USER manages USER, admins
                  invisible). The ADMIN route-guard stays on /announcements and
                  /settings. */}
              <Route path="users" element={<Users />} />
              <Route path="profile" element={<Profile />} />
              <Route path="chiamate" element={<ChiamatePage />} />
              {comune.features.archive && (
                <Route path="archivio" element={<FileStoragePage />} />
              )}
              <Route path="kpi" element={<KPIMetricsPage />} />
              {/* Analytics dashboard shipped dark: reachable by URL only, not in the nav. */}
              <Route path="analytics" element={<AnalyticsPage />} />
              {comune.features.officeHours && (
                <Route path="orari" element={<OrariUfficiPage />} />
              )}
              {comune.features.transferHours && (
                <Route path="orari-trasferimento" element={<OrariTrasferimentoPage />} />
              )}
              {comune.features.events && (
                <Route path="eventi" element={<EventiPage />} />
              )}
              <Route
                path="settings"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <SettingsPage />
                  </ProtectedRoute>
                }
              />
              <Route
                path="announcements"
                element={
                  <ProtectedRoute requiredRole="ADMIN">
                    <AnnouncementsPage />
                  </ProtectedRoute>
                }
              />
            </Route>

            {/* Se un utente digita un URL che non esiste, viene reindirizzato alla home. Corretto. */}
            <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
